import React, { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';

const Orders = () => {
  const { authFetch } = useUser();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('current');
  const [expandedOrderIds, setExpandedOrderIds] = useState([]);
  const [filterDate, setFilterDate] = useState('');
  
  // State for price editing
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [newPrice, setNewPrice] = useState('');

  const fetchOrders = async () => {
    try {
      const res = await authFetch('/orders');
      setOrders(res || []);
    } catch (err) {
      console.error('Ошибка загрузки заказов:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const markOrderAsReady = async (orderId) => {
    try {
      await authFetch(`/orders/${orderId}/ready`, { method: 'PATCH' });
      fetchOrders();
    } catch (err) {
      console.error('Ошибка обновления заказа:', err);
    }
  };

  const handleUpdatePrice = async (orderId) => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) {
      alert('Введите корректную цену.');
      return;
    }

    try {
      await authFetch(`/orders/${orderId}/update-price`, {
        method: 'PATCH',
        body: JSON.stringify({ newTotal: price }),
      });
      // После успешного обновления заново получаем все заказы, чтобы данные были консистентны
      fetchOrders(); 
      setEditingOrderId(null);
      setNewPrice('');
    } catch (error) {
      console.error("Failed to update order price:", error);
      alert(`Ошибка обновления цены: ${error.message || 'ошибка сервера'}`);
    }
  };

  const startEditingPrice = (order) => {
    setEditingOrderId(order.id);
    setNewPrice(order.total.toString());
  };

  const cancelEditing = () => {
    setEditingOrderId(null);
    setNewPrice('');
  };

  const toggleExpand = (orderId) => {
    setExpandedOrderIds(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const filterByDate = (order) => {
    if (!filterDate) return true;
    const delivery = new Date(order.deliveryDate).toISOString().split('T')[0];
    return delivery === filterDate;
  };
  
  const currentOrders = orders
    .filter(o => !o.isReady && !o.isCancelled)
    .filter(filterByDate);
  
  const readyOrders = orders
    .filter(o => o.isReady || o.isCancelled)
    .filter(filterByDate)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  
  if (loading) return <div className="text-white p-4">Загрузка заказов...</div>;

  return (
    <div className="text-gray-200 p-4 md:p-8">
      <div className="w-full max-w-6xl mx-auto space-y-6">
        <h1 className="text-3xl font-bold text-white">Заказы</h1>
        <div className="flex items-center gap-4">
  <label className="text-sm text-gray-300">
    Фильтр по дате доставки:
  </label>
  <input
    type="date"
    value={filterDate}
    onChange={(e) => setFilterDate(e.target.value)}
    className="bg-gray-700 text-white text-sm px-3 py-1 rounded"
  />
  {filterDate && (
    <button
      onClick={() => setFilterDate('')}
      className="text-sm text-red-400 hover:text-red-300"
    >
      ✖ Очистить
    </button>
  )}
</div>


        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('current')}
            className={`py-2 px-4 text-sm font-medium transition-colors duration-150 ${activeTab === 'current' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-500 hover:text-gray-300'}`}
          >
            Текущие заказы
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2 px-4 text-sm font-medium transition-colors duration-150 ${activeTab === 'history' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-500 hover:text-gray-300'}`}
          >
            История заказов
          </button>
        </div>

        <div className="p-4 bg-gray-800 bg-opacity-60 rounded-lg min-h-[200px] space-y-4">
          {activeTab === 'current' && (
            <>
              {currentOrders.length === 0 ? (
                <p className="text-gray-400">Нет текущих заказов 🕓</p>
              ) : (
                currentOrders.map(order => {
                  const isExpanded = expandedOrderIds.includes(order.id);

                  return (
                    <div
                      key={order.id}
                      className="bg-gray-800 border border-gray-700 rounded-md p-4 shadow"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-sm text-gray-300">
                          Имя: <span className="text-white font-semibold">{order.userId?.firstName || order.userId?.username || 'Неизвестно'}</span>
                        </p>
                        <p className="text-xs text-yellow-400 font-semibold">🟡 В работе</p>
                      </div>

                      <p className="text-sm text-gray-300 mb-1">
                        Номер заказа: <span className="text-white font-semibold">#{order.shortCode || order.id}</span>
                      </p>

                      <div className="text-sm text-gray-300 mb-1 flex items-center gap-4">
                        Сумма: 
                        {editingOrderId === order.id ? (
                          <div className="flex items-center gap-2">
                             <input
                              type="number"
                              value={newPrice}
                              onChange={(e) => setNewPrice(e.target.value)}
                              className="bg-gray-900 border border-gray-600 rounded-md text-white px-2 py-1 w-24 text-sm"
                            />
                            <button onClick={() => handleUpdatePrice(order.id)} className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 rounded">Сохранить</button>
                            <button onClick={cancelEditing} className="bg-gray-600 hover:bg-gray-700 text-white text-xs px-2 py-1 rounded">Отмена</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-green-400 font-semibold">{order.total?.toLocaleString()} ₽</span>
                            {!order.isCancelled && (
                              <button onClick={() => startEditingPrice(order)} className="text-blue-400 hover:text-blue-300 text-xs">(изменить)</button>
                            )}
                          </div>
                        )}
                      </div>

                      <p className="text-sm text-gray-300 mb-1">
                        Состав: <span className="text-white">{order.items.map(i => `${i.name} × ${i.quantity}`).join(', ')}</span>
                      </p>

                      {order.comment && (
                        <p className="text-sm text-gray-300 mb-1">
                          Комментарий: <span className="italic text-white">{order.comment}</span>
                        </p>
                      )}

                      <p className="text-sm text-gray-300 mb-1">
                        Приборы: <span className="text-white font-semibold">{order.needsCutlery ? 'Да' : 'Нет'}</span>
                      </p>
                      <p className="text-sm text-gray-300 mb-1">
                        Хлеб: <span className="text-white font-semibold">
                          {order.needsBread 
                            ? (order.breadType 
                                ? (order.breadType === 'white' ? 'Белый' : 'Черный') 
                                : 'Да (тип не указан)') 
                            : 'Нет'}
                        </span>
                      </p>
                      <p className="text-sm text-gray-300 mb-4">
                        Доставить к: <span className="text-white">{new Date(order.deliveryDate).toLocaleDateString()}</span>
                      </p>

                      <div className="flex gap-3">
                        <button
                          onClick={async () => {
                            await markOrderAsReady(order.id);
                            alert(`Не забудьте подписать заказ №${order.shortCode || order.id}`);
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm shadow"
                        >
                          ✅ Заказ готов
                        </button>

                        <button
                          onClick={() => toggleExpand(order.id)}
                          className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm"
                        >
                          {isExpanded ? '🔽 Скрыть' : '🔼 Показать детали'}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          {order.items.map(item => (
                            <div
                              key={item.id}
                              className="bg-gray-900 rounded p-2 flex flex-col items-center text-center"
                            >
                              <img
                                src={item.imageUrl || '/placeholder.jpg'}
                                alt={item.name}
                                className="w-full h-32 object-cover rounded mb-2"
                              />
                              <p className="text-sm font-semibold">{item.name}</p>
                              <p className="text-xs text-gray-400">{item.quantity} шт.</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </>
          )}

          {activeTab === 'history' && (
            <>
              {readyOrders.length === 0 ? (
                <p className="text-gray-400">История заказов пуста 🗃️</p>
              ) : (
                readyOrders.map(order => (
                  <div
                    key={order.id}
                    className="bg-gray-800 border border-gray-700 rounded-md p-4 shadow"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm text-gray-300">
                        Имя: <span className="text-white font-semibold">{order.userId?.firstName || order.userId?.username || 'Неизвестно'}</span>
                      </p>
                      <p className={`text-xs font-semibold ${order.isCancelled ? 'text-red-400' : 'text-green-400'}`}>
                        {order.isCancelled ? '❌ Отменён' : '✅ Готов'}
                      </p>
                    </div>
                    <p className="text-sm text-gray-300 mb-1">
                      Номер заказа: <span className="text-white font-semibold">#{order.shortCode || order.id}</span>
                    </p>
                    <p className="text-sm text-gray-300 mb-1">
                      Сумма: <span className="text-green-400 font-semibold">{order.total?.toLocaleString()} ₽</span>
                    </p>

                    <p className="text-sm text-gray-300 mb-1">
                      Состав: <span className="text-white">{order.items.map(i => `${i.name} × ${i.quantity}`).join(', ')}</span>
                    </p>
                    {order.comment && (
                      <p className="text-sm text-gray-300 mb-1">
                        Комментарий: <span className="italic text-white">{order.comment}</span>
                      </p>
                    )}
                    <p className="text-sm text-gray-300 mb-1">
                      Приборы: <span className="text-white font-semibold">{order.needsCutlery ? 'Да' : 'Нет'}</span>
                    </p>
                    <p className="text-sm text-gray-300 mb-1">
                      Хлеб: <span className="text-white font-semibold">
                        {order.needsBread 
                          ? (order.breadType 
                              ? (order.breadType === 'white' ? 'Белый' : 'Черный') 
                              : 'Да (тип не указан)') 
                          : 'Нет'}
                      </span>
                    </p>
                    <p className="text-sm text-gray-300">
                      Доставлен к: <span className="text-white">{new Date(order.deliveryDate).toLocaleDateString()}</span>
                    </p>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Orders;
